import React from 'react';
import { PageSection, SiteData, PricingPlanItem } from '../../../types';
import { Check, Plus, MessageCircle, Phone, ArrowRight } from 'lucide-react';
import { formatPriceDisplay, buildActionUrl } from '../../../utils/editorHelpers';
import { getOptimalTextColor, isDarkBackground } from '../../../utils/contrast';

interface SectionProps {
  section: PageSection;
  siteData: SiteData;
  onNavigate?: (slug: string) => void;
}

export const PricingSection: React.FC<SectionProps> = ({ section, siteData, onNavigate }) => {
  const content = section.content || {};
  const style = section.style || {};

  const badge = content.badge || 'FİYATLANDIRMA PAKETLERİ';
  const title = content.title || 'İhtiyacınıza Uygun Esnek Planlar';
  const description = content.description || 'Şeffaf fiyatlandırma politikamız ile bütçenize ve hedeflerinize en uygun paketi seçin.';
  
  const rawItems: any[] = Array.isArray(content.items) ? content.items : [];

  const bgColor = style.backgroundColor || '#FFFFFF';
  const textColor = style.textColor || getOptimalTextColor(bgColor);
  const isDark = isDarkBackground(bgColor);
  const accentColor = siteData.settings?.accentColor || '#2563EB';

  const handleButtonClick = (plan: PricingPlanItem) => {
    const linkType = plan.buttonLinkType || 'page';
    const targetUrl = buildActionUrl(linkType, plan.buttonLink, {
      whatsappPhone: plan.whatsappPhone || siteData.settings?.socialLinks?.whatsapp || siteData.settings?.phone,
      whatsappMessage: plan.whatsappMessage || `Merhaba, ${plan.title} paketi hakkında detaylı bilgi almak istiyorum.`,
      phoneNumber: plan.phoneNumber || siteData.settings?.phone,
      emailAddress: plan.emailAddress || siteData.settings?.email,
    });

    if (linkType === 'whatsapp' || linkType === 'phone' || linkType === 'email' || linkType === 'url') {
      window.open(targetUrl, '_blank', 'noopener,noreferrer');
    } else if (onNavigate) {
      onNavigate(plan.buttonLink || '/iletisim');
    }
  };

  return (
    <section
      id={style.anchorId || 'fiyatlar'}
      className={`py-20 px-4 sm:px-6 lg:px-8 transition-all ${style.customClass || ''}`}
      style={{ backgroundColor: bgColor, color: textColor }}
    >
      <div className={`mx-auto space-y-12 ${style.containerWidth === 'full' ? 'w-full' : 'max-w-7xl'}`}>
        {/* Section Header */}
        <div className="text-center max-w-2xl mx-auto space-y-3">
          {badge && (
            <div
              className="inline-block px-3.5 py-1 rounded-full text-xs font-extrabold uppercase tracking-wider bg-black/5"
              style={{ color: accentColor }}
            >
              {badge}
            </div>
          )}
          <h2 className="text-2xl sm:text-4xl font-extrabold tracking-tight">{title}</h2>
          {description && (
            <p className="text-sm opacity-75 max-w-xl mx-auto leading-relaxed">{description}</p>
          )}
        </div>

        {/* Pricing Cards Grid */}
        {rawItems.length === 0 ? (
          <div className="text-center py-12 px-4 rounded-3xl border-2 border-dashed border-slate-300 max-w-md mx-auto space-y-3">
            <p className="text-sm font-semibold text-slate-500">Henüz fiyat paketi eklenmemiş.</p>
            <p className="text-xs text-slate-400">Sağ paneldeki &quot;İçerik&quot; sekmesinden yeni bir paket ekleyebilirsiniz.</p>
          </div>
        ) : (
          <div className={`grid grid-cols-1 ${rawItems.length === 2 ? 'md:grid-cols-2 max-w-4xl' : rawItems.length >= 4 ? 'sm:grid-cols-2 lg:grid-cols-4' : 'md:grid-cols-3'} gap-8 mx-auto items-stretch`}>
            {rawItems.map((plan: PricingPlanItem, idx: number) => {
              const isPop = Boolean(plan.isPopular);
              const priceInfo = formatPriceDisplay(plan);
              const badgeLabel = plan.badgeText || (isPop ? 'EN ÇOK TERCİH EDİLEN' : '');
              const btnLabel = plan.buttonText || 'Teklif Al / Başla';

              return (
                <div
                  key={plan.id || idx}
                  className={`p-8 rounded-3xl border flex flex-col justify-between space-y-8 transition-all duration-300 ${
                    isPop
                      ? 'border-blue-600 bg-slate-900 text-white shadow-2xl scale-[1.03] ring-2 ring-blue-500/30 relative z-10'
                      : isDark
                      ? 'border-white/10 bg-slate-900/90 text-white shadow-sm hover:shadow-md'
                      : 'border-slate-200 bg-white text-slate-900 shadow-sm hover:shadow-md'
                  }`}
                >
                  {badgeLabel && (
                    <div
                      className="absolute -top-3.5 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full text-[10px] font-black uppercase tracking-wider text-white shadow-md whitespace-nowrap"
                      style={{ backgroundColor: accentColor }}
                    >
                      {badgeLabel}
                    </div>
                  )}

                  <div className="space-y-5">
                    <div className="space-y-1">
                      <h3 className="font-extrabold text-xl tracking-tight">{plan.title}</h3>
                      {plan.subtitle && (
                        <p className={`text-xs ${isPop ? 'text-slate-300' : 'text-slate-500'}`}>{plan.subtitle}</p>
                      )}
                    </div>

                    {/* Price Header */}
                    <div className="flex items-baseline gap-1.5 flex-wrap">
                      <span className="text-3.5xl sm:text-4xl font-black tracking-tight leading-none">
                        {priceInfo.displayAmount}
                      </span>
                      {priceInfo.displayPeriod && (
                        <span className={`text-xs font-semibold ${isPop ? 'text-slate-300' : 'text-slate-500'}`}>
                          {priceInfo.displayPeriod}
                        </span>
                      )}
                    </div>

                    {plan.description && (
                      <p
                        className={`text-xs leading-relaxed ${
                          isPop ? 'text-slate-300' : 'text-slate-600'
                        }`}
                      >
                        {plan.description}
                      </p>
                    )}

                    {/* Features checklist */}
                    {plan.features && plan.features.length > 0 && (
                      <div className="space-y-2.5 pt-5 border-t border-slate-500/20">
                        {plan.features.map((f: string, i: number) => (
                          <div key={i} className="flex items-start gap-2.5 text-xs font-medium">
                            <Check
                              className={`w-4 h-4 shrink-0 mt-0.5 ${
                                isPop ? 'text-cyan-400' : 'text-blue-600'
                              }`}
                            />
                            <span className="leading-snug">{f}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Action CTA Button */}
                  <button
                    type="button"
                    onClick={() => handleButtonClick(plan)}
                    className={`w-full py-3.5 px-4 rounded-xl font-extrabold text-xs shadow-md transition-all flex items-center justify-center gap-2 hover:scale-[1.02] active:scale-[0.98] ${
                      isPop
                        ? 'bg-blue-600 hover:bg-blue-500 text-white shadow-blue-500/20'
                        : 'bg-slate-900 hover:bg-slate-800 text-white shadow-slate-900/10'
                    }`}
                  >
                    <span>{btnLabel}</span>
                    {plan.buttonLinkType === 'whatsapp' ? (
                      <MessageCircle className="w-3.5 h-3.5 text-emerald-400" />
                    ) : plan.buttonLinkType === 'phone' ? (
                      <Phone className="w-3.5 h-3.5 text-cyan-300" />
                    ) : (
                      <ArrowRight className="w-3.5 h-3.5" />
                    )}
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
};
